export type AlertPermission = NotificationPermission | 'unsupported';

export type AlertNotificationOptions = {
  title: string;
  body: string;
  icon: string;
  tag: string;
  requireInteraction: boolean;
  silent: boolean;
};

export type AlertNotificationHandle = {
  close: () => void;
};

export type NotificationGateway = {
  permission: () => AlertPermission;
  requestPermission: () => Promise<AlertPermission>;
  show: (
    options: AlertNotificationOptions,
    onClick: () => void,
  ) => AlertNotificationHandle;
};

export type PageAttentionGateway = {
  isActive: () => boolean;
  focus: () => void;
};

export type RingtoneGateway = {
  unlock: () => Promise<boolean>;
  play: () => Promise<boolean>;
  stop: () => void;
};

export type IncomingCallAlertStatus = {
  soundReady: boolean;
  notificationPermission: AlertPermission;
};

type IncomingCallAlertDependencies = {
  ringtone: RingtoneGateway;
  notifications: NotificationGateway;
  page: PageAttentionGateway;
  openCall: () => void;
};

export class IncomingCallAlertController {
  private caller = '';
  private notification: AlertNotificationHandle | null = null;
  private soundReady = false;

  constructor(private readonly dependencies: IncomingCallAlertDependencies) {}

  getStatus(): IncomingCallAlertStatus {
    return {
      soundReady: this.soundReady,
      notificationPermission: this.dependencies.notifications.permission(),
    };
  }

  async unlockSound(): Promise<IncomingCallAlertStatus> {
    this.soundReady = await this.dependencies.ringtone.unlock();
    return this.getStatus();
  }

  async prepare(): Promise<IncomingCallAlertStatus> {
    const permissionRequest = this.dependencies.notifications.permission() === 'default'
      ? this.dependencies.notifications.requestPermission()
      : Promise.resolve(this.dependencies.notifications.permission());
    const [soundReady] = await Promise.all([
      this.dependencies.ringtone.unlock(),
      permissionRequest,
    ]);
    this.soundReady = soundReady;

    return this.getStatus();
  }

  start(caller: string): void {
    const normalizedCaller = String(caller || '').trim() || 'Номер скрыт';

    if (this.caller === normalizedCaller) {
      this.syncPageAttention();
      return;
    }

    if (this.caller) this.stop();
    this.caller = normalizedCaller;
    void this.dependencies.ringtone.play().then(ready => {
      this.soundReady = ready;
      if (!this.caller) this.dependencies.ringtone.stop();
    });
    this.syncPageAttention();
  }

  syncPageAttention(): void {
    if (!this.caller) return;

    if (this.dependencies.page.isActive()) {
      this.closeNotification();
      return;
    }

    if (
      this.notification
      || this.dependencies.notifications.permission() !== 'granted'
    ) return;

    try {
      this.notification = this.dependencies.notifications.show({
        title: 'Входящий звонок',
        body: `Номер: ${this.caller}`,
        icon: '/intez-logo-light.png',
        tag: 'intez-helpdesk-incoming-call',
        requireInteraction: true,
        silent: true,
      }, () => {
        this.dependencies.page.focus();
        this.dependencies.openCall();
        this.closeNotification();
      });
    } catch {
      this.notification = null;
    }
  }

  stop(): void {
    if (!this.caller && !this.notification) return;
    this.caller = '';
    this.dependencies.ringtone.stop();
    this.closeNotification();
  }

  private closeNotification(): void {
    this.notification?.close();
    this.notification = null;
  }
}

type BrowserAudioContextConstructor = typeof AudioContext;

class WebAudioRingtone implements RingtoneGateway {
  private context: AudioContext | null = null;
  private loop: number | null = null;
  private oscillators = new Set<OscillatorNode>();
  private playing = false;
  private requested = false;

  async unlock(): Promise<boolean> {
    const context = this.ensureContext();
    if (!context) return false;

    try {
      if (context.state === 'suspended') await context.resume();
      return String(context.state) === 'running';
    } catch {
      return false;
    }
  }

  async play(): Promise<boolean> {
    this.requested = true;
    if (this.playing) return String(this.context?.state) === 'running';
    const ready = await this.unlock();
    if (!ready || !this.context || !this.requested) return false;
    if (this.playing) return true;

    this.playing = true;
    this.playCycle();
    this.loop = window.setInterval(() => this.playCycle(), 2_800);
    return true;
  }

  stop(): void {
    this.requested = false;
    this.playing = false;
    if (this.loop !== null) window.clearInterval(this.loop);
    this.loop = null;
    this.oscillators.forEach(oscillator => {
      try {
        oscillator.stop();
      } catch {
        // Осциллятор уже мог завершиться самостоятельно.
      }
    });
    this.oscillators.clear();
  }

  private ensureContext(): AudioContext | null {
    if (this.context) return this.context;
    if (typeof window === 'undefined') return null;

    const AudioContextClass = window.AudioContext
      || (window as Window & { webkitAudioContext?: BrowserAudioContextConstructor }).webkitAudioContext;
    if (!AudioContextClass) return null;

    try {
      this.context = new AudioContextClass();
      return this.context;
    } catch {
      return null;
    }
  }

  private playCycle(): void {
    const context = this.context;
    if (!context || context.state !== 'running' || !this.playing) return;
    this.playTone(context.currentTime, 0.46);
    this.playTone(context.currentTime + 0.68, 0.46);
  }

  private playTone(startAt: number, duration: number): void {
    const context = this.context;
    if (!context) return;

    const master = context.createGain();
    master.gain.setValueAtTime(0.0001, startAt);
    master.gain.exponentialRampToValueAtTime(0.055, startAt + 0.025);
    master.gain.setValueAtTime(0.055, startAt + duration - 0.04);
    master.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
    master.connect(context.destination);

    [440, 480].forEach(frequency => {
      const oscillator = context.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, startAt);
      oscillator.connect(master);
      this.oscillators.add(oscillator);
      oscillator.addEventListener('ended', () => this.oscillators.delete(oscillator), { once: true });
      oscillator.start(startAt);
      oscillator.stop(startAt + duration);
    });
  }
}

class BrowserNotificationGateway implements NotificationGateway {
  permission(): AlertPermission {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return window.Notification.permission;
  }

  async requestPermission(): Promise<AlertPermission> {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    try {
      return await window.Notification.requestPermission();
    } catch {
      return window.Notification.permission;
    }
  }

  show(options: AlertNotificationOptions, onClick: () => void): AlertNotificationHandle {
    const notification = new window.Notification(options.title, {
      body: options.body,
      icon: options.icon,
      tag: options.tag,
      requireInteraction: options.requireInteraction,
      silent: options.silent,
    });
    notification.onclick = () => onClick();
    return { close: () => notification.close() };
  }
}

export function createBrowserIncomingCallAlerts(openCall: () => void) {
  return new IncomingCallAlertController({
    ringtone: new WebAudioRingtone(),
    notifications: new BrowserNotificationGateway(),
    page: {
      isActive: () => document.visibilityState === 'visible' && document.hasFocus(),
      focus: () => window.focus(),
    },
    openCall,
  });
}
